import {find, times} from 'lodash';

import {objectId} from 'shared/utils';
import {Course, Institution} from 'src/types/store';
import {
  CURRENT_SCHOOL_YEAR,
  getPeriodAbbreviation,
  finalGradingPeriodMap,
} from 'shared/portfolio/utils';
import {initGradesForPeriod} from 'portfolio/actions/courses';

import {CourseForm} from './course-form-wrapper';

export const newCourseObject = (institution: Institution): CourseForm => ({
  _id: objectId(),
  courseType: institution.type === 'CollegeCourseProvider' ? 'Dual Enrollment' : 'Regular',
  name: '',
  subject: '',
  institution: {
    name: institution.name,
    parentId: institution.parentId,
    country: institution.country,
    type: institution.type,
    _id: institution._id,
  },
  credits: '1',
  schoolYear: CURRENT_SCHOOL_YEAR.name,
  periodType: institution.gradeSettings.periodType,
  gradeType: institution.gradeSettings.gradeType,
  hasFinalGrade: institution.gradeSettings.hasFinalGrade,
  grades: initGradesForPeriod(
    institution.gradeSettings.periodType,
    institution.gradeSettings.gradeType,
    institution.gradeSettings.hasFinalGrade
  ),
  new: true,
});

export const courseFormFromCourse = (course: Course): CourseForm => {
  const gradeSettings = getGradeSettingsFromCourse(course);

  return {
    _id: course._id,
    courseType: course.courseType,
    name: course.name,
    subject: course.subject,
    institution: {
      name: course.institution.name,
      parentId: course.institution.parentId,
      country: course.institution.country,
      type: course.institution.type,
      _id: course.institution._id,
    },
    credits: `${course.credits}`,
    schoolYear: course.schoolYear,
    periodType: gradeSettings.periodType,
    gradeType: gradeSettings.gradeType,
    hasFinalGrade: gradeSettings.hasFinalGrade,
    grades: course.grades,
  };
};

const getPeriodTypeFromCourse = (course: Course): Institution['gradeSettings']['periodType'] => {
  if (find(course.grades, {period: 'W'})) return 'W';
  if (find(course.grades, {period: 'S'})) return 'S';

  return `${course.periodsPerYear}`;
};

export const getGradeSettingsFromCourse = (course: Course): Institution['gradeSettings'] => ({
  gradeType: course.grades.length === 0 ? 'L' : course.grades[0].gradeType,
  hasFinalGrade: !!find(course.grades, {period: 'F'}),
  periodType: getPeriodTypeFromCourse(course),
});

export const courseGroupingKey = (course: Course) => ({
  ...getGradeSettingsFromCourse(course),
  institutionId: course.institution._id,
});

export const courseGroupingKeyFromInstitution = (institution: Institution) => ({
  gradeType: institution.gradeSettings.gradeType,
  hasFinalGrade:
    institution.gradeSettings.periodType === '0' || institution.gradeSettings.hasFinalGrade,
  periodType: institution.gradeSettings.periodType,
  institutionId: institution._id,
});

const headerAndRowData = (periodType: Institution['gradeSettings']['periodType']) => {
  const parsedPeriodType = parseInt(periodType) || 0;
  const summerOrWinter = periodType === 'W' || periodType === 'S';
  const nonFinalGradeCount = parsedPeriodType + (summerOrWinter ? 1 : 0);

  return {
    parsedPeriodType,
    summerOrWinter,
    nonFinalGradeCount,
  };
};

export const getGradeSettingHeaders = ({periodType}: Institution['gradeSettings']) => {
  const {parsedPeriodType, summerOrWinter, nonFinalGradeCount} = headerAndRowData(periodType);
  const periodAbbreviation = getPeriodAbbreviation(parsedPeriodType);
  const headers: string[] = [];

  times(parsedPeriodType, (i) => headers.push(`${periodAbbreviation}${i + 1}`));

  if (summerOrWinter) {
    headers.push(finalGradingPeriodMap[periodType]);
  }

  times(4 - nonFinalGradeCount, () => headers.push(''));

  return headers;
};

export const getGradeRow = (course: Course, {periodType}: Institution['gradeSettings']) => {
  const {parsedPeriodType, summerOrWinter, nonFinalGradeCount} = headerAndRowData(periodType);
  const row: string[] = [];

  if (parsedPeriodType > 0) {
    course.grades.forEach((grade) => {
      if (grade.period !== 'F') {
        row.push(grade.gradeInput || '');
      }
    });
  }

  if (summerOrWinter) {
    row.push(course.grades.at(-1)?.gradeInput || '');
  }

  times(4 - nonFinalGradeCount, () => row.push(''));

  return row;
};
